import React, { useCallback, useEffect, useState } from "react";
import "./CreateEditOrganization.scss";
import Button from "devextreme-react/button";
import TextBox from "devextreme-react/text-box";
import DateBox from "devextreme-react/date-box";
import TextArea from "devextreme-react/text-area";
import SelectBox from "devextreme-react/select-box";
import PropTypes from "prop-types";
import LoadPanel from "devextreme-react/load-panel";
import classNames from "classnames";
import { closeIconSVG } from "../../Assets/SVGs/environmentSVGs";
import { orgDataPropTypes } from "../../Constants/propTypes";
import { useUser } from "../../contexts/UserContext";
import Notification from "../Notification/Notification";
import { notifyMultipleErrors } from "../../utilities";
import { Roles } from "../../Constants/constants";
import orgValidations from "./organizationValidations";

const currentDate = new Date();
const maxDescriptionLength = 500;
const maxOrganizationNameLength = 50;

const orgErrorMessages = {
  name: {
    error: false,
    message: "Organization Name must be in between 3 and 50 characters length",
  },
  website: { error: false, message: "Please enter a valid website" },
  date: { error: false, message: "Date is required" },
  phone: { error: false, message: "Please enter a valid phone number" },
  description: {
    error: false,
    message: "Description must be in between 3 and 500 characters length",
  },
  email: { error: false, message: "Please enter a valid Email id" },
};

const CreateEditOrganization = ({
  mode,
  setMode,
  closePopup,
  parentOrgId,
  currentOrgData,
  setCurrentOrgData,
  setOrganizationCreated,
}) => {
  const [organizationId, setOrganizationId] = useState(null);
  const [organizationName, setOrganizationName] = useState("");
  const [organizationWebsite, setOrganizationWebsite] = useState("");
  const [organizationDateCreated, setOrganizationDateCreated] = useState(null);
  const [organizationPhone, setOrganizationPhone] = useState("");
  const [organizationEmail, setOrganizationEmail] = useState("");
  const [organizationDescription, setOrganizationDescription] = useState("");
  const [organizationGroupId, setOrganizationGroupId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saveDisable, setSaveDisable] = useState(true);
  const [errorMessages, setErrorMessages] = useState(orgErrorMessages);
  const [availableGroups, setAvailableGroups] = useState([]);

  const { userRoles, backend } = useUser();

  const isSuperUser = userRoles?.includes(Roles.SUPER_USER);
  const isOrgAdmin = userRoles?.includes(Roles.ORG_ADMIN);

  useEffect(() => {
    if (isSuperUser || isOrgAdmin) {
      backend.callAllUserGroups().then((res) => {
        if (res.data.length > 0)
          setAvailableGroups(
            res?.data?.map((group) => {
              const text = `${group.displayName} - ${group.id}`;
              return { ...group, text };
            })
          );
      });
    }
  }, [backend, isOrgAdmin, isSuperUser]);

  const validateInputs = (type, value) => {
    const isInputValid = orgValidations[type](value);

    setErrorMessages((previousErrorStatus) => ({
      ...previousErrorStatus,
      [type]: {
        ...previousErrorStatus[type],
        error: !isInputValid,
      },
    }));
  };

  const renderErrorBlock = (errorObj) => (
    <div className="field-err-msg">{errorObj.error && errorObj.message}</div>
  );

  useEffect(() => {
    if (mode === "Edit" && currentOrgData) {
      const {
        id,
        name,
        website,
        createDate,
        phone,
        email,
        organizationDetails,
        groupId,
      } = currentOrgData;

      setOrganizationId(id);
      setOrganizationName(name);
      setOrganizationWebsite(website);
      setOrganizationDateCreated(createDate);
      setOrganizationPhone(phone);
      setOrganizationEmail(email);
      setOrganizationDescription(organizationDetails);
      setOrganizationGroupId(groupId);
    }
  }, [currentOrgData, mode]);

  const clearFormFields = useCallback(() => {
    setOrganizationId(null);
    setOrganizationName("");
    setOrganizationWebsite("");
    setOrganizationDateCreated(null);
    setOrganizationPhone("");
    setOrganizationEmail("");
    setOrganizationDescription("");
    setOrganizationGroupId(null);
    setErrorMessages(orgErrorMessages);
  }, []);

  const handlePopupClose = useCallback(() => {
    closePopup();
    setMode("Create");
    setSaveDisable(true);
    clearFormFields();
  }, [clearFormFields, closePopup, setMode]);

  const isFormSavable = useCallback(
    () =>
      errorMessages.name.error ||
      errorMessages.website.error ||
      errorMessages.date.error ||
      errorMessages.phone.error ||
      errorMessages.email.error ||
      errorMessages.description.error,
    [errorMessages]
  );

  const isSaveDisabled = useCallback(() => {
    if (mode === "Create") {
      if (
        organizationName.trim() &&
        organizationWebsite.trim() &&
        organizationPhone.trim() &&
        organizationDescription.trim() &&
        organizationEmail.trim() &&
        organizationDateCreated
      ) {
        setSaveDisable(isFormSavable);
      } else {
        setSaveDisable(true);
      }
    }

    if (mode === "Edit" && organizationName) {
      if (
        organizationName.trim() !== currentOrgData.name ||
        organizationWebsite.trim() !== currentOrgData.website ||
        organizationPhone.trim() !== currentOrgData.phone ||
        organizationDescription.trim() !== currentOrgData.organizationDetails ||
        organizationEmail.trim() !== currentOrgData.email ||
        organizationDateCreated !== currentOrgData.createDate ||
        organizationGroupId !== currentOrgData.groupId
      ) {
        setSaveDisable(isFormSavable);
      } else {
        setSaveDisable(true);
      }
    }
  }, [
    mode,
    isFormSavable,
    currentOrgData,
    organizationName,
    organizationWebsite,
    organizationPhone,
    organizationDescription,
    organizationEmail,
    organizationDateCreated,
    organizationGroupId,
  ]);

  useEffect(() => {
    isSaveDisabled();
  }, [isSaveDisabled]);

  const handleOrganizationCreate = useCallback(() => {
    setIsLoading(true);
    setSaveDisable(true);

    const parentId = parentOrgId;
    const groupId = organizationGroupId;

    const createDate = `${organizationDateCreated.toLocaleDateString("fr-ca")}T00:00:00`;

    const orgData = {
      name: organizationName,
      organizationDetails: organizationDescription,
      email: organizationEmail,
      phone: organizationPhone,
      website: organizationWebsite,
      createDate,
      parentId,
      groupId,
    };

    if (!organizationGroupId) {
      backend
        .callCreatePartnerOrganization(orgData)
        .then(() => {
          Notification("Partner organization created successfully", "success");
          handlePopupClose();
          setOrganizationCreated(true);
        })
        .catch((err) => {
          if (err?.response?.data?.errors) {
            notifyMultipleErrors(err.response.data.errors);
          } else if (err?.response?.data?.message) {
            Notification(err.response.data.message, "error");
          } else {
            Notification(err.Message || err.message, "error");
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      backend
        .callSuperUserCreateOrganization(orgData)
        .then(() => {
          Notification("Partner organization created successfully", "success");
          handlePopupClose();
          setOrganizationCreated(true);
        })
        .catch((err) => {
          if (err?.response?.data?.errors) {
            notifyMultipleErrors(err.response.data.errors);
          } else if (err?.response?.data?.message) {
            Notification(err.response.data.message, "error");
          } else {
            Notification(err.Message || err.message, "error");
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [
    backend,
    parentOrgId,
    organizationGroupId,
    organizationDateCreated,
    organizationName,
    organizationDescription,
    organizationEmail,
    organizationPhone,
    organizationWebsite,
    handlePopupClose,
    setOrganizationCreated,
  ]);

  const handleOrganizationEdit = useCallback(() => {
    setIsLoading(true);

    const editOrganizationData = {
      ...currentOrgData,
      name: organizationName,
      website: organizationWebsite,
      createDate: organizationDateCreated,
      phone: organizationPhone,
      email: organizationEmail,
      organizationDetails: organizationDescription,
      groupId: organizationGroupId,
    };

    if (isSuperUser) {
      backend
        .callSuperUserUpdateOrganization(organizationId, editOrganizationData)
        .then(() => {
          Notification("Partner organization updated successfully", "success");
          handlePopupClose();
          setCurrentOrgData(editOrganizationData);
        })
        .catch((err) => {
          if (err?.response?.data?.errors) {
            notifyMultipleErrors(err.response.data.errors);
          } else if (err?.response?.data?.message) {
            Notification(err.response.data.message, "error");
          } else {
            Notification(err.Message || err.message, "error");
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      backend
        .callUpdateOrganization(organizationId, editOrganizationData)
        .then(() => {
          Notification("Partner organization updated successfully", "success");
          handlePopupClose();
          setCurrentOrgData(editOrganizationData);
        })
        .catch((err) => {
          if (err?.response?.data?.errors) {
            notifyMultipleErrors(err.response.data.errors);
          } else if (err?.response?.data?.message) {
            Notification(err.response.data.message, "error");
          } else {
            Notification(err.Message || err.message, "error");
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [
    currentOrgData,
    organizationName,
    organizationWebsite,
    organizationDateCreated,
    organizationPhone,
    organizationEmail,
    organizationDescription,
    organizationGroupId,
    isSuperUser,
    backend,
    organizationId,
    handlePopupClose,
    setCurrentOrgData,
  ]);

  return (
    <div
      id="createeditorganization-popup"
      className="create-edit-organization-popup-content"
    >
      <div className="popup-header">
        <div className="header-title">{mode} organization</div>
        <div className="header-actions">
          <div id="createeditorganization-popup-close-btn" className="close-action">
            <Button onClick={handlePopupClose} stylingMode="text">
              {closeIconSVG}
            </Button>
          </div>
        </div>
      </div>
      <div className="popup-content">
        <div className="create-edit-organization-form">
          <div className="organization-form">
            <div className="form-fields">
              <div className="fields-wrapper">
                <div className="organization-details">
                  <div className="form-layout two-col-layout">
                    <div className="layout-item left-col">
                      <div className="col-content">
                        <div className="organization-name form-field">
                          <div className="field-title">
                            Name <span className="required-asterisk">*</span>
                          </div>
                          <div className="field-input">
                            <TextBox
                              value={organizationName}
                              id="createeditorganization-name"
                              placeholder="Name"
                              maxLength={maxOrganizationNameLength}
                              className={classNames({
                                "error-field": errorMessages.name.error,
                              })}
                              onKeyUp={(e) => {
                                setOrganizationName(e?.event?.target?.value);
                                validateInputs("name", e?.event?.target?.value);
                              }}
                            />
                          </div>
                          {renderErrorBlock(errorMessages.name)}
                        </div>
                        <div className="form-row fields-group">
                          <div className="organization-date form-field">
                            <div className="field-title">
                              Date created <span className="required-asterisk">*</span>
                            </div>
                            <div className="field-input">
                              <DateBox
                                value={organizationDateCreated}
                                id="createeditorganization-date"
                                max={currentDate}
                                acceptCustomValue={false}
                                placeholder="Date"
                                className={classNames({
                                  "error-field": errorMessages.date.error,
                                })}
                                onValueChanged={(e) => {
                                  setOrganizationDateCreated(e?.value);
                                  validateInputs("date", e?.value);
                                }}
                              />
                            </div>
                            {renderErrorBlock(errorMessages.date)}
                          </div>
                          <div className="organization-number form-field">
                            <div className="field-title">
                              Phone number <span className="required-asterisk">*</span>
                            </div>
                            <div className="field-input">
                              <TextBox
                                value={organizationPhone}
                                id="createeditorganization-phone"
                                placeholder="Phone"
                                className={classNames({
                                  "error-field": errorMessages.phone.error,
                                })}
                                onKeyUp={(e) => {
                                  setOrganizationPhone(e?.event?.target?.value);
                                  validateInputs("phone", e?.event?.target?.value);
                                }}
                              />
                            </div>
                            {renderErrorBlock(errorMessages.phone)}
                          </div>
                        </div>
                        <div className="organization-email form-field">
                          <div className="field-title">
                            Email <span className="required-asterisk">*</span>
                          </div>
                          <div className="field-input">
                            <TextBox
                              value={organizationEmail}
                              id="createeditorganization-email"
                              placeholder="Email"
                              className={classNames({
                                "error-field": errorMessages.email.error,
                              })}
                              onKeyUp={(e) => {
                                setOrganizationEmail(e?.event?.target?.value);
                                validateInputs("email", e?.event?.target?.value);
                              }}
                            />
                          </div>
                          {renderErrorBlock(errorMessages.email)}
                        </div>
                      </div>
                    </div>
                    <div className="layout-item right-col">
                      <div className="col-content">
                        <div className="organization-email form-field">
                          <div className="field-title">
                            Website <span className="required-asterisk">*</span>
                          </div>
                          <div className="field-input">
                            <TextBox
                              value={organizationWebsite}
                              id="createeditorganization-website"
                              placeholder="Website"
                              className={classNames({
                                "error-field": errorMessages.website.error,
                              })}
                              onKeyUp={(e) => {
                                setOrganizationWebsite(e?.event?.target?.value);
                                validateInputs("website", e?.event?.target?.value);
                              }}
                            />
                          </div>
                          {renderErrorBlock(errorMessages.website)}
                        </div>
                        <div className="organization-description form-field">
                          <div className="field-title">
                            Organization details{" "}
                            <span className="required-asterisk">*</span>
                          </div>
                          <div className="field-input">
                            <TextArea
                              value={organizationDescription}
                              id="createeditorganization-description"
                              placeholder="Write a description"
                              className={classNames({
                                "error-field": errorMessages.description.error,
                              })}
                              onKeyUp={(e) => {
                                setOrganizationDescription(e?.event?.target?.value);
                                validateInputs("description", e?.event?.target?.value);
                              }}
                              maxLength={maxDescriptionLength}
                            />
                            <div className="length-indicator">
                              {organizationDescription?.length}/{maxDescriptionLength}
                            </div>
                          </div>
                          {renderErrorBlock(errorMessages.description)}
                        </div>
                      </div>
                    </div>
                  </div>
                  {availableGroups && (
                    <div className="azure-group-id-dropdown-wrapper">
                      <div className="organization-group-id form-field">
                        <div className="field-title">Azure AD group id</div>
                        <div className="field-input">
                          <SelectBox
                            value={organizationGroupId}
                            dataSource={availableGroups}
                            displayExpr="text"
                            valueExpr="id"
                            searchEnabled={true}
                            searchMode="contains"
                            searchExpr="text"
                            showDataBeforeSearch={false}
                            onValueChanged={(e) => {
                              setOrganizationGroupId(e.value);
                            }}
                            placeholder="Group id"
                            id="createeditorganization-azure-id"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="form-actions">
                  <div className="form-buttons">
                    <Button
                      id="createeditorganization-cancel-btn"
                      text="Cancel"
                      className="form-cancel btn-outlined"
                      onClick={handlePopupClose}
                    />
                    <Button
                      id="createeditorganization-add-or-save-btn"
                      className="form-save btn-default"
                      text={mode === "Create" ? mode : "Save"}
                      onClick={
                        mode === "Create"
                          ? handleOrganizationCreate
                          : handleOrganizationEdit
                      }
                      disabled={saveDisable}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <LoadPanel position="center" visible={isLoading} showPane={true} />
    </div>
  );
};

CreateEditOrganization.propTypes = {
  mode: PropTypes.string.isRequired,
  setMode: PropTypes.func.isRequired,
  closePopup: PropTypes.func.isRequired,
  parentOrgId: PropTypes.number.isRequired,
  currentOrgData: orgDataPropTypes,
  setCurrentOrgData: PropTypes.func.isRequired,
  setOrganizationCreated: PropTypes.func.isRequired,
};

export default CreateEditOrganization;
